// File: src/features/admin/OrderlistApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface OrderItem {
  id: number;
  product_id: number;
  product_name: string | null;
  product_quantity: number;
  unit_price: string;
  discount_type: number;
  discount: string;
  itemTotal: string;
  discountAmt: string;
  taxAmount: string;
  shippingTotal: string;
}

export interface OrderComment {
  id?: number;
  order_id: number;
  user_type: 'admin' | 'user';
  user_id: number;
  user_name?: string;
  comment: string;
  created_at?: string;
  status?: number;
}

export interface Order {
  id: number;
  user_id: number | string | null | undefined;
  user_name: string;
  order_date: string;
  total_amount: string;
  total_order_amount: string;
  total_shipping_charge: string;
  total_discount_amount: string;
  total_tax_amount: string;
  status: number;
  status_text: string;
  delivery_address_id: number;
  delivery_model_id: number | null;
  items: OrderItem[];
  payment_status?: string;
  comments?: OrderComment[];
  comment?: string;
  pickup_code?: string;
  qr_code?: string;
  order_type?: 'regular' | 'guest' | string;
  orderType?: string;
  type?: string;
  guest_order?: number | boolean | string;
  is_guest?: number | boolean | string;
}

export interface OrdersResponse {
  error: boolean;
  message: string;
  orders: Order[];
}

export interface OrderStatusUpdateRequest {
  order_id: number;
  status: number;
  comment?: string;
}

export interface DeleteOrderRequest {
  order_id: number;
}

export interface OrderCommentsRequest {
  order_id: number;
}

export interface OrderCommentsResponse {
  error: boolean;
  message: string;
  comments: OrderComment[];
}

export interface DeleteCommentRequest {
  comment_id: number;
  order_id: number;
}

export interface GenerateQRRequest {
  order_id: number;
  order_type: 'regular' | 'guest';
}

export interface GenerateQRResponse {
  success: boolean;
  message: string;
  pickup_code: string;
  qr_code: string;
  order_id?: string;
  order_type?: string;
}

export const ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Payment Pending', color: '#ff9800' },
  { value: 1, label: 'Payment Success ', color: '#2196f3' },
  { value: 2, label: 'Processing', color: '#4caf50' },
  { value: 3, label: 'Pickup Pending', color: '#795548' },
  { value: 4, label: 'Ware House Pickup', color: '#f44336' },
  { value: 5, label: 'In Transit', color: '#511111ff' },
  { value: 6, label: 'Out for delivery', color: '#427489ff' },
  { value: 7, label: 'Delivered', color: '#7ca14dff' },
  { value: 8, label: 'Not delivered', color: '#c474a0ff' },
  { value: 9, label: 'Cancelled by user', color: '#f4559aff' },
  { value: 10, label: 'Return and replacement', color: '#c6b0ecff' },
  { value: 11, label: 'Return and refund', color: '#2b061aff' },
  { value: 12, label: 'Order cancelled by admin', color: '#96e8deff' },
  { value: 13, label: 'Order Delayed', color: '#ff002bff' },
  { value: 20, label: 'Payment failed', color: '#edc573ff' },
];

// ----------------- Helpers -----------------

const parseCommentsString = (commentString: string): OrderComment[] => {
  if (!commentString || typeof commentString !== 'string') return [];

  const commentParts = commentString.split(/(?=\d+\.\s*\[)/);

  return commentParts
    .filter((part) => part.trim())
    .map((part, index) => {
      const dateMatch = part.match(/\[([^\]]+)\]/);
      const userTypeMatch = part.match(/\(([^)]+)\)/);
      const commentMatch = part.match(/- (.+?)(?=\s*\d+\.\s*\[|$)/s);

      const userType = userTypeMatch?.[1] === 'admin' ? 'admin' : 'user';
      const dateString = dateMatch?.[1];
      let parsedDate: string;

      try {
        parsedDate = dateString ? new Date(dateString).toISOString() : new Date().toISOString();
      } catch {
        parsedDate = new Date().toISOString();
      }

      const contentHash = btoa(commentMatch?.[1]?.trim() || part).slice(0, 8);
      const uniqueId =
        Date.now() +
        index +
        Math.random() * 1000 +
        parseInt(contentHash.replace(/[^0-9]/g, '') || '0', 10);

      return {
        id: Math.floor(uniqueId),
        order_id: 0,
        user_type: userType as 'admin' | 'user',
        user_id: userType === 'admin' ? 1 : 0,
        user_name: userType === 'admin' ? 'Admin' : 'User',
        comment:
          commentMatch?.[1]?.trim() ||
          part.replace(/^\d+\.\s*\[.*?\]\s*\([^)]+\)\s*-\s*/, '').trim(),
        created_at: parsedDate,
        status: undefined,
      };
    });
};

const extractStatusFromComment = (commentText: string): number | undefined => {
  const statusKeywords: Record<string, number> = {
    cancelled: 9,
    cancel: 9,
    'return and replacement': 7,
    replacement: 7,
    'return and refund': 8,
    refund: 8,
    'not delivered': 5,
    delayed: 10,
    delivered: 4,
    transit: 2,
    processing: 1,
    pending: 0,
  };

  const lowerComment = commentText.toLowerCase();

  for (const [keyword, status] of Object.entries(statusKeywords)) {
    if (lowerComment.includes(keyword)) return status;
  }

  return undefined;
};

// Helper function to determine order type from order object
export const determineOrderType = (order: any): 'regular' | 'guest' => {
  const normalize = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim().toLowerCase();
    return s;
  };

  console.log('🔍 RAW Order Data for type determination:', {
    id: order?.id,
    user_id: order?.user_id,
    user_name: order?.user_name,
    order_type: order?.order_type,
    orderType: order?.orderType,
    type: order?.type,
    guest_order: order?.guest_order,
    is_guest: order?.is_guest,
    keys: order ? Object.keys(order) : [],
  });

  const possibleTypeFields = [
    order?.order_type,
    order?.orderType,
    order?.type,
    order?.order_type_text,
    order?.orderTypeText,
  ];
  for (const field of possibleTypeFields) {
    const n = normalize(field);
    if (!n) continue;
    if (n.includes('guest')) return 'guest';
    if (n.includes('regular')) return 'regular';
  }

  const guestFlags = [order?.guest_order, order?.is_guest];
  for (const f of guestFlags) {
    const n = normalize(f);
    if (!n) continue;
    if (n === '1' || n === 'true' || n === 'yes') return 'guest';
  }

  const userIdRaw = order?.user_id ?? order?.customer_id ?? '';
  const userIdStr = normalize(userIdRaw);
  if (!userIdStr || userIdStr === '0' || userIdStr === 'null' || userIdStr === 'undefined' || userIdStr === 'none') {
    console.log(' Detected guest by user_id pattern:', userIdRaw);
    return 'guest';
  }

  const userName = normalize(order?.user_name);
  if (userName) {
    if (
      userName.includes('guest') ||
      userName.includes('visitor') ||
      userName === 'anonymous' ||
      userName === 'guest user'
    ) {
      console.log(' Detected guest by user_name:', order?.user_name);
      return 'guest';
    }
  }

  console.log(' No strong guest indicators found, defaulting to regular for order:', order?.id);
  return 'regular';
};

// ----------------- API -----------------

type ListOrdersArg = { warehouse_id?: number | '' } | void;

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers, { type }) => {
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      if (type === 'mutation') {
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
      }
      return headers;
    },
  }),
  tagTypes: ['Order', 'OrderComments'],
  endpoints: (build) => ({
    // ------------- UPDATED: listOrders (regular + guest, supports warehouse_id) -------------
    listOrders: build.query<OrdersResponse, ListOrdersArg>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new URLSearchParams();

        const warehouseId =
          arg && typeof arg === 'object' && 'warehouse_id' in arg ? (arg as any).warehouse_id : undefined;

        if (
          warehouseId !== undefined &&
          warehouseId !== null &&
          warehouseId !== '' &&
          !Number.isNaN(Number(warehouseId))
        ) {
          formData.append('warehouse_id', String(warehouseId));
        }

        console.log('📦 listOrders query, warehouse_id =', warehouseId || '(ALL)');

        const commonReq = {
          method: 'POST' as const,
          body: formData,
        };

        // Regular orders
        const regularRes = await fetchWithBQ({
          url: 'user/adminlistOrders',
          ...commonReq,
        });

        if (regularRes.error) {
          console.error('❌ listOrders regular endpoint failed:', regularRes.error);
          return { error: regularRes.error as any };
        }

        // Guest orders (best-effort; if it fails we still return regular orders)
        const guestRes = await fetchWithBQ({
          url: 'user/adminGuestListOrders',
          ...commonReq,
        });

        if (guestRes.error) {
          console.warn('⚠️ adminGuestListOrders failed, returning only regular orders.', guestRes.error);
        }

        const rawRegular = (regularRes.data || {}) as any;
        const rawGuest = (!guestRes.error && guestRes.data ? guestRes.data : {}) as any;

        const extractOrders = (payload: any): any[] => {
          if (!payload) return [];
          if (Array.isArray(payload.orders)) return payload.orders;
          if (Array.isArray(payload.data)) return payload.data;
          if (Array.isArray(payload.order_list)) return payload.order_list;
          return [];
        };

        const combinedRawOrders = [
          ...extractOrders(rawRegular),
          ...extractOrders(rawGuest),
        ];

        console.log(' Raw API Combined Orders:', JSON.stringify(combinedRawOrders, null, 2));

        const transformedOrders: Order[] = combinedRawOrders.map((order: any) => {
          console.log(`\n Processing Order #${order.id}:`, {
            raw_data: order,
            user_id: order.user_id,
            user_name: order.user_name,
            all_keys: Object.keys(order || {}),
          });

          let comments: OrderComment[] = [];

          if (Array.isArray(order.comments)) {
            comments = order.comments;
          } else if (typeof order.comment === 'string' && order.comment.trim()) {
            comments = parseCommentsString(order.comment).map((comment) => ({
              ...comment,
              order_id: order.id,
              status: extractStatusFromComment(comment.comment),
            }));
          } else if (typeof order.comments === 'string' && order.comments.trim()) {
            comments = parseCommentsString(order.comments).map((comment) => ({
              ...comment,
              order_id: order.id,
              status: extractStatusFromComment(comment.comment),
            }));
          }

          const orderType = determineOrderType(order);

          console.log(` Order #${order.id} final determination:`, {
            determined_type: orderType,
            user_id: order.user_id,
            user_name: order.user_name,
          });

          return {
            ...order,
            comments,
            order_type: orderType,
          } as Order;
        });

        console.log('\n All Orders Summary:', transformedOrders.map((o) => ({
          id: o.id,
          order_type: o.order_type,
          user_id: o.user_id,
          user_name: o.user_name,
        })));

        const message =
          rawRegular?.message ||
          rawGuest?.message ||
          (warehouseId ? 'Orders fetched successfully for selected warehouse' : 'Orders fetched successfully');

        return {
          data: {
            error: rawRegular?.error || false,
            message,
            orders: transformedOrders,
          },
        };
      },
      providesTags: ['Order'],
    }),

    // ----------------- Mutations & other queries (unchanged) -----------------

    updateOrderStatus: build.mutation<OrdersResponse, OrderStatusUpdateRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());
        formData.append('status', data.status.toString());
        if (data.comment) formData.append('comment', data.comment);

        return {
          url: 'user/updateOrderStatus',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Order', 'OrderComments'],
    }),

    deleteOrder: build.mutation<any, DeleteOrderRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/deleteOrder',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Order'],
    }),

    getOrderComments: build.query<OrderCommentsResponse, OrderCommentsRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/getOrderComments',
          method: 'POST',
          body: formData,
        };
      },
      providesTags: ['OrderComments'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        comments: response.comments || [],
      }),
    }),

    deleteComment: build.mutation<any, DeleteCommentRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('comment_id', data.comment_id.toString());
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/deleteComment',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['OrderComments', 'Order'],
    }),

    // generateQR & getQrImage (unchanged)
    generateQR: build.mutation<GenerateQRResponse, GenerateQRRequest>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const orderType =
          arg.order_type === 'guest' || arg.order_type === 'regular' ? arg.order_type : 'regular';
        const primaryUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr`;
        const fallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr`;

        console.log('generateQR -> trying primary URL', primaryUrl);

        let res = await fetchWithBQ({ url: primaryUrl, method: 'GET' });

        if ((res.error && (res.error as any).status === 404) || !res.data) {
          console.warn('generateQR primary failed, trying fallback', { primary: res, fallbackUrl });
          res = await fetchWithBQ({ url: fallbackUrl, method: 'GET' });
        }

        if (res.error) {
          console.error('generateQR both endpoints failed', res.error);
          return { error: res.error as any };
        }

        try {
          const data = res.data as any;
          const transformed: GenerateQRResponse = {
            success: data.success || false,
            message: data.message || 'QR CODE generated successfully',
            pickup_code: data.pickup_code || data.pickupCode || '',
            qr_code: data.qr_code || data.qrCode || data.qr || '',
            order_id: data.order_id,
            order_type: data.order_type || orderType,
          };
          console.log('generateQR success', transformed);
          return { data: transformed };
        } catch (err) {
          console.error('generateQR transform failed', err);
          return { error: { status: 'CUSTOM_TRANSFORM_ERROR', error: String(err) } as any };
        }
      },
      invalidatesTags: ['Order'],
    }),

    getQrImage: build.query<Blob, { order_id: number; order_type: 'regular' | 'guest' }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const orderType = arg.order_type || 'regular';
        const primaryUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr/image`;
        const fallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr/image`;

        console.log('getQrImage -> trying primary URL', primaryUrl);

        let res = await fetchWithBQ({
          url: primaryUrl,
          method: 'GET',
          headers: { Accept: 'image/png' },
          // @ts-ignore
          responseHandler: (response: Response) => response.blob(),
          cache: 'no-cache' as RequestCache,
        } as any);

        if ((res.error && (res.error as any).status === 404) || !res.data) {
          console.warn('getQrImage primary failed, trying fallback', { primary: res, fallbackUrl });
          res = await fetchWithBQ({
            url: fallbackUrl,
            method: 'GET',
            headers: { Accept: 'image/png' },
            // @ts-ignore
            responseHandler: (response: Response) => response.blob(),
            cache: 'no-cache' as RequestCache,
          } as any);
        }

        if (res.error) {
          console.error('getQrImage both endpoints failed', res.error);
          return { error: res.error as any };
        }

        try {
          const blob = res.data as Blob;
          console.log('getQrImage success, blob size:', blob.size);
          return { data: blob };
        } catch (err) {
          console.error('getQrImage transform failed', err);
          return { error: { status: 'CUSTOM_TRANSFORM_ERROR', error: String(err) } as any };
        }
      },
    }),
  }),
});

export const {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useGetOrderCommentsQuery,
  useDeleteCommentMutation,
  useGenerateQRMutation,
  useGetQrImageQuery,
  useLazyGetQrImageQuery,
} = orderApi;
